import React from "react";
import { ButtonBase, Stack, Typography, Select, MenuItem } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { tokens } from "@/tokens";

interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  maxVisiblePages?: number;
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  maxVisiblePages = 7,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startItem = Math.min((page - 1) * limit + 1, total);
  const endItem = Math.min(page * limit, total);

  const getVisiblePages = (): (number | "ellipsis")[] => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | "ellipsis")[] = [1];
    const half = Math.floor((maxVisiblePages - 2) / 2);
    let start = Math.max(2, page - half);
    let end = Math.min(totalPages - 1, page + half);

    if (page <= half + 1) {
      end = maxVisiblePages - 2;
    }
    if (page >= totalPages - half) {
      start = totalPages - maxVisiblePages + 3;
    }

    if (start > 2) pages.push("ellipsis");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("ellipsis");
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const pageButtonSx = {
    width: 24,
    height: 24,
    borderRadius: "5px",
    fontSize: tokens.fontSize.xs,
    fontWeight: tokens.fontWeight.semibold,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 150ms ease",
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        borderTop: `1px solid ${tokens.border.light}`,
        bgcolor: tokens.bg.surface,
        px: 2,
        py: 1,
      }}
    >
      {/* Left: Showing text */}
      <Stack direction="row" alignItems="center" gap={2}>
        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
          Showing {startItem} to {endItem} of {total}
        </Typography>
        {onLimitChange && (
          <Select
            size="small"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            sx={{
              fontSize: tokens.fontSize.xs,
              height: 24,
              color: tokens.text.secondary,
              "& .MuiSelect-select": { py: "2px", px: 1 },
            }}
          >
            {[10, 20, 25, 50].map((n) => (
              <MenuItem key={n} value={n} sx={{ fontSize: tokens.fontSize.xs }}>
                {n} rows
              </MenuItem>
            ))}
          </Select>
        )}
      </Stack>

      {/* Right: Page buttons */}
      <Stack direction="row" alignItems="center" gap={0.5}>
        {/* Prev */}
        <ButtonBase
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          sx={{
            ...pageButtonSx,
            color: page <= 1 ? tokens.text.disabled : tokens.text.secondary,
            "&:hover": { bgcolor: page <= 1 ? "transparent" : tokens.bg.muted },
          }}
        >
          <ChevronLeft sx={{ fontSize: 16 }} />
        </ButtonBase>

        {getVisiblePages().map((p, idx) =>
          p === "ellipsis" ? (
            <Typography
              key={`ellipsis-${idx}`}
              sx={{ ...pageButtonSx, color: tokens.text.muted, cursor: "default" }}
            >
              ...
            </Typography>
          ) : (
            <ButtonBase
              key={p}
              onClick={() => onPageChange(p)}
              sx={{
                ...pageButtonSx,
                ...(p === page
                  ? { bgcolor: tokens.text.primary, color: tokens.bg.surface }
                  : { color: tokens.text.secondary, "&:hover": { bgcolor: tokens.bg.muted } }),
              }}
            >
              {p}
            </ButtonBase>
          )
        )}

        {/* Next */}
        <ButtonBase
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          sx={{
            ...pageButtonSx,
            color: page >= totalPages ? tokens.text.disabled : tokens.text.secondary,
            "&:hover": {
              bgcolor: page >= totalPages ? "transparent" : tokens.bg.muted,
            },
          }}
        >
          <ChevronRight sx={{ fontSize: 16 }} />
        </ButtonBase>
      </Stack>
    </Stack>
  );
};

export default Pagination;
